import { Box, RadioGroup, Radio, FormControlLabel } from '@mui/material'
import Field from 'components/ui/Field'
import OcrBuilderSelector from 'components/UploadDialog/OcrBuilderSelector'
import { DOC_TYPE, DocType } from 'config'
import { UserGroupConfig, Mode } from 'types'

import { DocTypeOption } from './types'

interface Props {
  mode: Mode
  labelWidth?: number
  onChange: (value: string) => void
  docTypeOptions: DocTypeOption[]
  docType: DocType | null
  ocrBuilderID: number | null
  onOcrBuilderIDChange: (id: number | null) => void
  userGroupConfig: UserGroupConfig
}

function DocTypeSelector(props: Props) {
  const {
    mode,
    labelWidth = 150,
    onChange,
    docTypeOptions,
    docType,
    ocrBuilderID,
    onOcrBuilderIDChange,
    userGroupConfig,
  } = props

  const showInvoice = userGroupConfig.useInvoiceOCR
  const showOrderForm = userGroupConfig.useOrderForm
  const showOcrBuilder = userGroupConfig.useOCRBuilder
  const showGeneral = mode === 'normal' && !userGroupConfig.useConcur

  const handleDocTypeSelect = (e) => onChange(e.target?.value)

  const generalDocTypeRadios = docTypeOptions
    .filter((opt) => !opt.disabled) // 非表示
    .map((opt, idx) => {
      const Icon = opt.icon
      const label = (
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Box
            sx={{
              mr: 1,
              minWidth: 160,
            }}
          >
            <Box>{opt.label}</Box>
            <Box
              sx={{
                fontSize: '0.75rem',
                color: 'grey.400',
              }}
            >
              {opt.description}
            </Box>
          </Box>
          {idx === 0 && (
            <Icon sx={{ fontSize: '3rem' }} color={opt.disabled ? 'disabled' : 'action'} />
          )}
        </Box>
      )
      return (
        <FormControlLabel
          sx={{
            '& + &': {
              mt: 2,
            },
          }}
          key={opt.value}
          label={label}
          value={opt.value}
          control={<Radio size="small" color="primary" disabled={opt.disabled} />}
        />
      )
    })

  return (
    <Field divider label="文書タイプ">
      {(showInvoice || showOrderForm) && (
        <Field
          inline
          labelWidth={labelWidth}
          label={<span style={{ fontWeight: 400 }}>特定帳票</span>}
        >
          <RadioGroup
            aria-label="docType"
            name="docType"
            value={docType}
            onChange={handleDocTypeSelect}
            data-testid="document-type-specific"
          >
            {generalDocTypeRadios}
          </RadioGroup>
        </Field>
      )}

      {showOcrBuilder && (
        <Field
          inline
          labelWidth={labelWidth}
          label={<span style={{ fontWeight: 400 }}>定形帳票</span>}
          description="Template OCR連携"
        >
          <FormControlLabel
            label="Template OCRテンプレートを利用"
            control={
              <Radio
                size="small"
                color="primary"
                name="docType"
                disabled={!userGroupConfig.useOCRBuilder}
                value={DOC_TYPE.OCR_BUILDER}
                checked={docType === DOC_TYPE.OCR_BUILDER}
                onChange={handleDocTypeSelect}
              />
            }
          />
          <OcrBuilderSelector
            disabled={docType !== DOC_TYPE.OCR_BUILDER}
            ocrBuilderID={ocrBuilderID}
            onOcrBuilderIDChange={onOcrBuilderIDChange}
          />
        </Field>
      )}

      {showGeneral && (
        <Field
          inline
          labelWidth={labelWidth}
          label={<span style={{ fontWeight: 400 }}>テキスト・表</span>}
        >
          <FormControlLabel
            label="イメージデータからテキストを読み取り"
            control={
              <Radio
                size="small"
                color="primary"
                name="docType"
                value={DOC_TYPE.GENERAL_OCR}
                checked={docType === DOC_TYPE.GENERAL_OCR}
                onChange={handleDocTypeSelect}
              />
            }
            data-testid="document-type-general"
          />
        </Field>
      )}
    </Field>
  )
}

export default DocTypeSelector
